/**
 * Auto-hide flyout close timing, without React.
 *
 * - Leaving starts a hold; re-entering cancels it.
 * - While the flyout's own menu is open (portaled, so the pointer "leaves"),
 *   no hold runs. Closing the menu away from the flyout starts one.
 * - `closeNow` is for a click on the canvas: no hold.
 *
 * Gallery SoT: /?test=maater-ui#panel-collapse
 */
import { SHELL_FLYOUT_ENTER_HOLD_MS, SHELL_FLYOUT_LEAVE_HOLD_MS } from '@/lib/shell-panel-motion'

export interface ShellFlyoutHold {
  enter(): void
  leave(): void
  menuOpenChange(open: boolean): void
  closeNow(): void
  dispose(): void
  readonly pending: boolean
}

export function createShellFlyoutHold(
  close: () => void,
  holdMs: number = SHELL_FLYOUT_LEAVE_HOLD_MS,
): ShellFlyoutHold {
  let timer: ReturnType<typeof setTimeout> | null = null
  let hovering = false
  let menuOpen = false

  const cancel = () => {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  const schedule = () => {
    cancel()
    timer = setTimeout(() => {
      timer = null
      if (!hovering && !menuOpen) close()
    }, holdMs)
  }

  return {
    enter() {
      hovering = true
      cancel()
    },
    leave() {
      hovering = false
      if (!menuOpen) schedule()
    },
    menuOpenChange(open: boolean) {
      menuOpen = open
      if (open) cancel()
      else if (!hovering) schedule()
    },
    closeNow() {
      cancel()
      close()
    },
    dispose: cancel,
    get pending() {
      return timer !== null
    },
  }
}

/** Opens only if the pointer is still on the rail when the timer ends. */
export interface ShellFlyoutEnterDelay {
  arm(): void
  cancel(): void
  dispose(): void
  readonly pending: boolean
}

export function createShellFlyoutEnterDelay(
  open: () => void,
  holdMs: number = SHELL_FLYOUT_ENTER_HOLD_MS,
): ShellFlyoutEnterDelay {
  let timer: ReturnType<typeof setTimeout> | null = null

  const cancel = () => {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  return {
    arm() {
      if (timer !== null) return
      timer = setTimeout(() => {
        timer = null
        open()
      }, holdMs)
    },
    cancel,
    dispose: cancel,
    get pending() {
      return timer !== null
    },
  }
}
